import {
  effectiveStrokeWidthMm,
  effectiveTextSizeMm,
  layerOutlineMm,
  type CanonicalDocument,
} from '@besbello/design-core';
import type { Diagnostic } from '../types.js';
import { ruleIdOf, type ValidationContext } from '../rules.js';

/**
 * El bordado no reproduce un trazo de 0,2 mm: la aguja tiene un grosor y el hilo
 * otro. El limite es un dato del proceso, medido sobre el trazo REAL (tras
 * aplicar la escala de la capa), no sobre el numero que escribio el disenador.
 */
export function validateStrokeAndText(doc: CanonicalDocument, ctx: ValidationContext): Diagnostic[] {
  const out: Diagnostic[] = [];
  const ruleId = ruleIdOf(ctx.technique);

  for (const layer of doc.layers) {
    if (layer.kind === 'shape') {
      const width = effectiveStrokeWidthMm(layer);
      if (width !== null && width > 0 && width < ctx.technique.minStrokeWidthMm) {
        out.push({
          code: 'STROKE_TOO_THIN',
          severity: 'error',
          layerId: layer.id,
          measured: Number(width.toFixed(3)),
          expected: ctx.technique.minStrokeWidthMm,
          unit: 'mm',
          ruleId,
        });
      }
    }

    if (layer.kind === 'text') {
      const size = effectiveTextSizeMm(layer);
      if (size < ctx.technique.minTextSizeMm) {
        out.push({
          code: 'TEXT_TOO_SMALL',
          severity: 'error',
          layerId: layer.id,
          measured: Number(size.toFixed(3)),
          expected: ctx.technique.minTextSizeMm,
          unit: 'mm',
          ruleId,
        });
      }

      /*
        El CONTORNO del texto es un trazo mas, y se rompe igual que el de una
        forma: en bordado un contorno de 0,2 mm no lo hace la aguja, y en
        serigrafia se cierra al secar. Se mide con la escala de la capa aplicada,
        como el de las formas — el numero escrito en el panel no vale, porque una
        capa al 200 % dobla su contorno.
      */
      if (layer.outline) {
        const escala = Math.hypot(layer.transform.a, layer.transform.b) || 1;
        const grosor = layer.outline.widthMm * escala;
        if (grosor < ctx.technique.minStrokeWidthMm) {
          out.push({
            code: 'STROKE_TOO_THIN',
            severity: 'error',
            layerId: layer.id,
            measured: Number(grosor.toFixed(3)),
            expected: ctx.technique.minStrokeWidthMm,
            unit: 'mm',
            ruleId,
          });
        }
      }

      /*
        ARCO que da mas de una vuelta: las letras se pisan y no se lee nada. Es
        un error del diseno, no del proceso, asi que no depende de la tecnica.
      */
      if (layer.arc) {
        const anchoAproximadoMm = effectiveTextSizeMm(layer) * 0.55 * [...layer.text].length;
        if (anchoAproximadoMm / layer.arc.radiusMm > 2 * Math.PI) {
          out.push({
            code: 'ARC_OVERLAP',
            severity: 'error',
            layerId: layer.id,
            measured: Number((anchoAproximadoMm / layer.arc.radiusMm).toFixed(3)),
            expected: Number((2 * Math.PI).toFixed(3)),
            unit: 'none',
            ruleId,
          });
        }
      }

      // R-4: el shaping no es reproducible entre versiones de motor. Mientras el
      // texto no este congelado, lo que se ve no es exactamente lo que saldra.
      if (!layer.frozenLayout) {
        out.push({
          code: 'TEXT_NOT_FROZEN',
          severity: 'info',
          layerId: layer.id,
          unit: 'none',
          ruleId,
        });
      }
    }

    if (layerOutlineMm(layer).estimated) {
      out.push({
        code: 'GEOMETRY_ESTIMATED',
        severity: 'info',
        layerId: layer.id,
        unit: 'none',
        ruleId,
      });
    }
  }

  return out;
}
